/* import __COLOCATED_TEMPLATE__ from './sync-banner.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';

import { StandalonePlatform } from 'embercom/objects/standalone/constants';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type AppService from 'embercom/services/app-service';

export interface Args {}

export default class StandaloneAnalyzeSyncBanner extends Component<Args> {
  @service declare appService: AppService & {
    app: { id: string; isStandaloneApp: boolean; standalone_platforms: any };
  };
  @service declare router: RouterService;
  @service declare intl: IntlService;

  @tracked isDrawerOpen = false;

  get app() {
    return this.appService.app;
  }

  get currentPlatform(): StandalonePlatform {
    return this.app.standalone_platforms.firstObject.platform_type as StandalonePlatform;
  }

  get isInAnalyzeRoutes(): boolean {
    let currentRoute = this.router.currentRouteName;
    let analyzeRoutes = [
      'apps.app.standalone.reports',
      'apps.app.standalone.performance',
      'apps.app.standalone.optimize-fin',
      'apps.app.standalone.topics-explorer',
      'apps.app.standalone.reports.custom-report',
    ];

    return analyzeRoutes.some((route) => currentRoute.startsWith(route));
  }

  get shouldShowBanner(): boolean {
    if (!this.app.isStandaloneApp) {
      return false;
    }

    if (!this.appService.mainStandalonePlatform.supportsHistoricalSyncing) {
      return false;
    }

    if (!this.isInAnalyzeRoutes) {
      return false;
    }

    if (this.appService.mainStandalonePlatform.showAnalyzeSectionConnectCta) {
      return false;
    }

    if (this.appService.mainStandalonePlatform.historicalSyncingEnabled) {
      return false;
    }

    return true;
  }

  get bannerText(): string {
    switch (this.currentPlatform) {
      case StandalonePlatform.Zendesk:
        return this.intl.t('standalone.analyze.sync-banner.zendesk.message');
      case StandalonePlatform.Salesforce:
        return this.intl.t('standalone.analyze.sync-banner.salesforce.message');
      default:
        return this.intl.t('standalone.analyze.sync-banner.generic.message');
    }
  }

  get buttonText(): string {
    switch (this.currentPlatform) {
      case StandalonePlatform.Zendesk:
        return this.intl.t('standalone.analyze.sync-banner.zendesk.button-text');
      case StandalonePlatform.Salesforce:
        return this.intl.t('standalone.analyze.sync-banner.salesforce.button-text');
      default:
        return this.intl.t('standalone.analyze.sync-banner.generic.button-text');
    }
  }

  @action openDrawer() {
    this.isDrawerOpen = true;
  }

  @action closeDrawer() {
    this.isDrawerOpen = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Analyze::SyncBanner': typeof StandaloneAnalyzeSyncBanner;
    'standalone/analyze/sync-banner': typeof StandaloneAnalyzeSyncBanner;
  }
}
